import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate } from 'react-intl';
import ReactSelect, { type MultiValue } from 'react-select';

import { CLOCK_SKEW_WARN_MS } from '../../constants/devices';
import { useDeviceEvents, useEventTypes, useIntl } from '../../hooks';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui';

const PAGE_SIZE = 20;

interface DeviceEventsDialogProps {
  deviceId: string;
  deviceSerialNumber: string;
  onOpenChange: (open: boolean) => void;
}

interface MessageTypeOption {
  value: string;
  label: string;
}

const ReactSelectComponent = ReactSelect as unknown as ComponentType<{
  value: MessageTypeOption[];
  onChange: (value: MultiValue<MessageTypeOption>) => void;
  options: MessageTypeOption[];
  isMulti: true;
  placeholder: string;
  className?: string;
  classNamePrefix?: string;
  isClearable?: boolean;
  styles?: SelectStyles;
}>;

type SelectStyles = {
  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => object;
  menu: (provided: Record<string, unknown>) => object;
  option: (provided: Record<string, unknown>, state: { isFocused: boolean }) => object;
  placeholder: (provided: Record<string, unknown>) => object;
  multiValueRemove: (provided: Record<string, unknown>) => object;
  multiValue: (provided: Record<string, unknown>) => object;
  multiValueLabel: (provided: Record<string, unknown>) => object;
};

const selectStyles: SelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    backgroundColor: 'var(--color-input)',
    boxShadow: state.isFocused ? '0 0 0 2px #3b82f6' : 'none',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--color-foreground)',
  }),
  multiValueRemove: (base) => ({
    ...base,
    ':hover': {
      backgroundColor: 'var(--color-destructive)',
      color: 'var(--color-white',
    },
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
};

export default function DeviceEventsDialog({
  deviceId,
  deviceSerialNumber,
  onOpenChange,
}: DeviceEventsDialogProps) {
  const intl = useIntl();
  const { data: eventTypes = [] } = useEventTypes(deviceId);
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [messageTypes, setMessageTypes] = useState<MessageTypeOption[]>([]);
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [appliedMessageTypes, setAppliedMessageTypes] = useState<string[]>([]);

  const clearFilters = () => {
    setPage(1);
    setFrom('');
    setTo('');
    setMessageTypes([]);
    setAppliedFrom('');
    setAppliedTo('');
    setAppliedMessageTypes([]);
  };

  useEffect(() => clearFilters(), [deviceId]);

  const { data, isLoading, isFetching, error, refetch } = useDeviceEvents({
    deviceId,
    page,
    pageSize: PAGE_SIZE,
    from: appliedFrom || undefined,
    to: appliedTo || undefined,
    messageTypes: appliedMessageTypes.length > 0 ? appliedMessageTypes : undefined,
  });

  const events = useMemo(() => data?.events ?? [], [data?.events]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFiltersApplied = Boolean(appliedFrom || appliedTo || appliedMessageTypes.length > 0);

  useEffect(() => {
    if (!data) {
      return;
    }

    const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [data, page, total]);

  const handleApplyFilters = () => {
    setAppliedFrom(from);
    setAppliedTo(to);
    setAppliedMessageTypes(messageTypes.map((opt) => opt.value));
    setPage(1);
  };

  const formatMessageTypeLabel = useCallback(
    (value: string) => {
      const key = `deviceEvents.messageTypes.${value}`;
      const label = intl.formatMessage({ id: key });
      return label === key ? value : label;
    },
    [intl],
  );

  const messageTypeOptions = useMemo<MessageTypeOption[]>(() => {
    return eventTypes.map((type) => ({
      value: type,
      label: formatMessageTypeLabel(type),
    }));
  }, [eventTypes, formatMessageTypeLabel]);

  const showingStart = events.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingEnd = events.length === 0 ? 0 : Math.min(page * PAGE_SIZE, total);

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[85vh] flex flex-col overflow-hidden">
        <DialogTitle className="text-lg font-semibold">
          {intl.formatMessage({ id: 'deviceEvents.title' }, { serial: deviceSerialNumber })}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {intl.formatMessage({ id: 'deviceEvents.description' })}
        </DialogDescription>

        <div className="flex flex-col gap-4 flex-1 overflow-hidden">
          <div className="flex flex-wrap items-end gap-3 bg-gray-light rounded-lg p-4">
            <div className="flex flex-col gap-1 min-w-[250px]">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'deviceEvents.filters.messageType' })}
              </label>
              <ReactSelectComponent
                value={messageTypes}
                onChange={(selected) => setMessageTypes([...selected])}
                options={messageTypeOptions}
                isMulti={true}
                placeholder={intl.formatMessage({ id: 'deviceEvents.filters.allTypes' })}
                classNamePrefix="message-type-select"
                isClearable={true}
                styles={selectStyles}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'deviceEvents.filters.fromDate' })}
              </label>
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-2 border border-border bg-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-grey-dark">
                {intl.formatMessage({ id: 'deviceEvents.filters.toDate' })}
              </label>
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="px-3 py-2 border border-border bg-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="default" size="sm" onClick={handleApplyFilters}>
                {intl.formatMessage({ id: 'deviceEvents.filters.apply' })}
              </Button>
              {hasFiltersApplied && (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  {intl.formatMessage({ id: 'deviceEvents.filters.reset' })}
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 rounded-lg bg-background-light overflow-hidden">
            <div className="overflow-auto h-full">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">
                      {intl.formatMessage({ id: 'deviceEvents.table.messageType' })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({ id: 'deviceEvents.table.payload' })}
                    </TableHead>
                    <TableHead className="w-64">
                      {intl.formatMessage({ id: 'deviceEvents.table.timestamps' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-6 text-foreground">
                        {intl.formatMessage({ id: 'deviceEvents.loading' })}
                      </TableCell>
                    </TableRow>
                  )}
                  {error && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-destructive py-4">
                        <div className="flex items-center gap-2">
                          <span>
                            {intl.formatMessage({ id: 'deviceEvents.failedToLoad' })}:{' '}
                            {error instanceof Error
                              ? error.message
                              : intl.formatMessage({ id: 'common.unknownError' })}
                          </span>
                          <Button variant="outline" size="sm" onClick={() => refetch()}>
                            {intl.formatMessage({ id: 'common.retry' })}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && !error && events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-foreground">
                        {intl.formatMessage({ id: 'deviceEvents.noEvents' })}
                      </TableCell>
                    </TableRow>
                  )}
                  {events.map((event) => (
                    <TableRow key={`${event.id}-${event.createdAt}`}>
                      <TableCell className="align-top">
                        <div className="flex flex-col gap-1">
                          <Badge variant="secondary" className="w-fit">
                            {formatMessageTypeLabel(event.messageType)}
                          </Badge>
                          <span className="text-[11px] text-foreground font-mono break-all">
                            {intl.formatMessage({ id: 'deviceEvents.eventId' })}: {event.id}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <pre className="bg-gray-light border border-border rounded p-2 text-xs text-foreground overflow-auto max-h-32 whitespace-pre-wrap">
                          {JSON.stringify(event.messagePayload, null, 2)}
                        </pre>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-foreground">
                            <FormattedDate
                              value={new Date(event.createdAt)}
                              year="numeric"
                              month="numeric"
                              day="numeric"
                              hour="2-digit"
                              minute="2-digit"
                              second="2-digit"
                            />
                          </span>
                          <span className="text-xs text-foreground">
                            {intl.formatMessage({ id: 'deviceEvents.messageInitiatedAt' })}:{' '}
                            <FormattedDate
                              value={new Date(event.messageInitiatedAt)}
                              year="numeric"
                              month="numeric"
                              day="numeric"
                              hour="2-digit"
                              minute="2-digit"
                              second="2-digit"
                            />
                            {Math.abs(
                              new Date(event.createdAt).getTime() -
                                new Date(event.messageInitiatedAt).getTime(),
                            ) > CLOCK_SKEW_WARN_MS && (
                              <Badge
                                variant="warning"
                                className="ml-2"
                                data-test-id="clock-skew-marker"
                                title={intl.formatMessage({ id: 'deviceEvents.clockSkew' })}
                              >
                                {intl.formatMessage({ id: 'deviceEvents.clockSkew' })}
                              </Badge>
                            )}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Badge variant={event.ackAt ? 'success' : 'secondary'} className="w-fit">
                            {intl.formatMessage({
                              id: event.ackAt
                                ? 'deviceEvents.acknowledged'
                                : 'deviceEvents.waitingAck',
                            })}
                          </Badge>
                          <span className="text-xs text-foreground">
                            {event.ackAt && (
                              <FormattedDate
                                value={new Date(event.ackAt)}
                                year="numeric"
                                month="numeric"
                                day="numeric"
                                hour="2-digit"
                                minute="2-digit"
                                second="2-digit"
                              />
                            )}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex items-center justify-between py-2 text-xs text-foreground mt-2 shrink-0">
            <span>
              {intl.formatMessage(
                { id: 'deviceEvents.pagination' },
                {
                  start: showingStart,
                  end: showingEnd,
                  total,
                },
              )}
              {isFetching && !isLoading && (
                <span className="ml-2 text-[11px] text-foreground">
                  {intl.formatMessage({ id: 'deviceEvents.refreshing' })}
                </span>
              )}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                {intl.formatMessage({ id: 'common.previous' })}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage(Math.min(totalPages, page + 1))}
              >
                {intl.formatMessage({ id: 'common.next' })}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
